import { SendLimitDefinition, WhatsAppSenderStatus } from 'modules/graphql/types';
import { WHATSAPP_SENDER_RESTRICTED_STATUSES } from 'modules/templateEditor/common/constants';
import styled, { css } from 'styled-components';
import { NegativeBadge, WarningBadge } from 'ui/badge';
import { ErrorFill16 } from 'ui/icons/aurora/16px/ErrorFill16';
import { WarningFill16 } from 'ui/icons/aurora/16px/WarningFill16';
import { colors, spacing } from 'ui/theme';
import { HelpTooltip } from 'ui/tooltip';
import { SETTINGS_DISPLAY_CONTENT } from '../../constants';

const { restrictedStatusTooltipContent, lowQualityTooltipContent } =
  SETTINGS_DISPLAY_CONTENT.sender;

const badgeStylesCommon = css`
  &&& > svg path {
    fill: currentcolor;
  }

  [data-test='badge-negative-inner'], [data-test='badge-warning-inner'] {
    display: flex;
    align-items: center;
    gap: ${spacing.quarter};

    [data-test="tooltip-trigger"] path {
      fill: none !important;
    }
  }
`;

const StyledNegativeBadge = styled(NegativeBadge)`
  ${badgeStylesCommon}
  color: ${colors.textDestructive};
  background-color: ${colors.backgroundDestructiveSubtle};
  outline-color: ${colors.borderDestructiveSubtle};
`;

const StyledWarningBadge = styled(WarningBadge)`
  ${badgeStylesCommon}
  color: ${colors.textWarning};
  background-color: ${colors.backgroundWarningSubtle};
  outline-color: ${colors.borderWarningSubtle};
`;

export type SenderStatusBadgeProps = {
  sendLimit?: SendLimitDefinition;
  status?: WhatsAppSenderStatus;
};

export const SenderStatusBadge = ({ sendLimit, status, ...props }: SenderStatusBadgeProps) => {
  if (!!status && WHATSAPP_SENDER_RESTRICTED_STATUSES.includes(status))
    return (
      <StyledNegativeBadge icon={<ErrorFill16 />} {...props}>
        Send limit reached
        <HelpTooltip
          automaticallyAvoidCollisions
          iconProps={{ height: 16, width: 16, isColorInherited: true }}
          tooltipContent={restrictedStatusTooltipContent(sendLimit)}
        />
      </StyledNegativeBadge>
    );
  if (status === WhatsAppSenderStatus.Flagged)
    return (
      <StyledWarningBadge icon={<WarningFill16 />} {...props}>
        Flagged
        <HelpTooltip
          automaticallyAvoidCollisions
          iconProps={{ height: 16, width: 16, isColorInherited: true }}
          tooltipContent={lowQualityTooltipContent}
        />
      </StyledWarningBadge>
    );
  return null;
};
